import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  ChartToothStateName,
  PatientFileWithGlance,
  PatientFlagSummary,
  ToothDetail,
  ToothGlyphName,
  TreatmentPlan,
  VisitSummary,
} from "@dentacore/shared-types";

import type { MoneyInCurrency, Permission } from "@dentacore/shared-types";
import { useCan } from "../auth/use-can";
import {
  getPatient,
  getTooth,
  getToothChart,
  listAttachments,
  listNotes,
  listVisits,
  markToothNormal,
  markToothRemoved,
  setPrimaryTeeth,
} from "../api/patients";
import { listPlans } from "../api/plans";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { standingOf } from "../plans/standing";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import { formatPhone } from "../ui/countries";
import { useSize } from "../ui/paging";
import { UndoToast } from "../ui/undo-toast";
import { useAnchor } from "../ui/use-anchor";
import { useDismiss } from "../ui/use-dismiss";
import { BillingTab } from "./billing-tab";
import { EditPatientModal } from "./edit-patient-modal";
import { FlagsModal } from "./flags-modal";
import { HistoryTab } from "./history-tab";
import { ImagingTab } from "./imaging-tab";
import { formatAmounts } from "./money";
import { NotesTab } from "./notes-tab";
import { panelDock, panelWidth } from "./chart-fit";
import styles from "./patient-file.module.css";
import { ToothChart } from "./tooth-chart";
import { ToothEditor } from "./tooth-editor";
import type { ToothAsk, ToothAskKind } from "./tooth-editor";
import { isPrimaryTooth, predecessorOf, replacedBaby, waitingBehind } from "./tooth-geometry";
import { nextAskId } from "./ring-ask";
import type { RingAsk } from "./ring-ask";
import { inProgress, Segments, TreatmentTab } from "./treatment-panel";
import { countsOf, formatDate, VisitDialog, visitRowData } from "./visit-dialog";

/**
 * **Six tabs** (PF5, 2026-09-27): Chart, Treatment, History, Notes, Imaging,
 * Billing.
 *
 * Chart is first because that is where a clinician looks. **Treatment** is its
 * own tab rather than a list under the chart, and **History** holds the visits
 * as well as the work charted at them — the recent-visits list the mockup puts
 * under the chart is gone: *"do I need to see the recent visits someone had?
 * Unless I ask to, I should not."*
 */
const TABS = ["Chart", "Treatment", "History", "Notes", "Imaging", "Billing"] as const;
type TabName = (typeof TABS)[number];

/**
 * What the quick ring can ask a tab to do (STORY-221): each one the tab's own
 * button — New note, Import images, Take payment, New invoice.
 */
type TabAsk = RingAsk<"note"> | RingAsk<"images"> | RingAsk<"pay" | "invoice">;

/** A tooth ask, and which tooth it is for — it waits for that tooth's panel. */
type FileToothAsk = ToothAsk & { fdi: number };

/** A tooth's second line in the ring: what the chart draws, as the legend says it. */
const TOOTH_RING_META: Record<ChartToothStateName, string> = {
  SOUND: "Not worked on",
  TO_BE_DONE: "To be done",
  DONE: "Done",
  MISSING: "Removed",
};

/**
 * What each tab needs (STORY-166). A tab its reader cannot open is not drawn —
 * the server refuses its reads regardless — and the counts on the tab row are
 * not fetched either, so a receptionist's file does not raise four refusals in
 * the background.
 */
const TAB_NEEDS: Record<TabName, Permission> = {
  Chart: "clinical.chart.view",
  Treatment: "plans.view",
  History: "clinical.chart.view",
  Notes: "clinical.notes.view",
  Imaging: "clinical.imaging.view",
  Billing: "billing.view",
};

/**
 * One patient file: a two-line header and six tabs, filling the window with no
 * scroll bar (PF4, PF6, PF7).
 *
 * **Everything a tab counts is loaded when the file opens** (PF12) — plans,
 * visits, notes and images — so each tab can say what it holds before it is
 * opened. Notes and Imaging still read their own lists when opened, and report
 * back when a write changes the count.
 */
export function PatientFileView({ patientId }: { patientId: string }): React.JSX.Element {
  const can = useCan();
  const navigate = useNavigate();
  const [file, setFile] = useState<PatientFileWithGlance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabName>("Chart");
  // `?tab=Notes` opens the file on that tab: the quick ring's way in (QR3).
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedTab = searchParams.get("tab");
  useEffect(() => {
    const found = TABS.find((name) => name === wantedTab);
    if (found !== undefined) setTab(found);
  }, [wantedTab, patientId]);
  const mayPlans = can("plans.view");
  const mayChart = can("clinical.chart.view");
  const mayNotes = can("clinical.notes.view");
  const mayImaging = can("clinical.imaging.view");
  const visibleTabs = TABS.filter((name) => can(TAB_NEEDS[name]));
  // The tab asked for, or the first this reader may open when it is closed to them.
  const shownTab: TabName | undefined = visibleTabs.includes(tab) ? tab : visibleTabs[0];
  /**
   * An image the visit dialog asked for, waiting for the Imaging tab to open it
   * (client's manual QA, item 29). Held here because the hand-off crosses two
   * tabs, and cleared by the receiving tab so coming back does not reopen it.
   */
  const [openImageId, setOpenImageId] = useState<string | null>(null);
  const [toothStates, setToothStates] = useState<Partial<Record<number, ChartToothStateName>>>({});
  const [toothGlyphs, setToothGlyphs] = useState<Partial<Record<number, ToothGlyphName[]>>>({});
  /** The parts of each tooth completed work touched — the chart's top views. */
  const [toothSurfaces, setToothSurfaces] = useState<Partial<Record<number, string[]>>>({});
  /** The parts planned work names — red on the same top views. */
  const [plannedSurfaces, setPlannedSurfaces] = useState<Partial<Record<number, string[]>>>({});
  /** Bumped after a write, to pull the chart again — see the effect below. */
  const [chartVersion, setChartVersion] = useState(0);
  const [selectedTooth, setSelectedTooth] = useState<number | null>(null);
  /** Whether the chart draws baby teeth — the server works it out (BT1–BT3). */
  const [primaryTeeth, setPrimaryTeethShown] = useState(false);
  /** The message after a baby tooth is replaced or put back, with its Undo (BT10). */
  const [notice, setNotice] = useState<{ id: number; message: string; undo?: () => void } | null>(
    null,
  );
  const [editing, setEditing] = useState(false);
  /** The flags editor (QA #37) — the first way to record a flag in the app. */
  const [editingFlags, setEditingFlags] = useState(false);

  const [plans, setPlans] = useState<readonly TreatmentPlan[] | null>(null);
  const [visits, setVisits] = useState<readonly VisitSummary[] | null>(null);
  const [noteCount, setNoteCount] = useState<number | null>(null);
  const [imageCount, setImageCount] = useState<number | null>(null);
  /** Treatments opened or closed by hand on the Treatment tab (PF8). */
  const [treatmentOpen, setTreatmentOpen] = useState<ReadonlyMap<string, boolean>>(new Map());
  /** The visit whose dialog is open, by its local date. */
  const [openVisit, setOpenVisit] = useState<string | null>(null);
  /** A tab's button the quick ring pressed, waiting for that tab to load (STORY-221). */
  const [tabAsk, setTabAsk] = useState<TabAsk | null>(null);
  /** A tooth-panel button the quick ring pressed, waiting for that tooth's panel. */
  const [toothAsk, setToothAsk] = useState<FileToothAsk | null>(null);
  const clearTabAsk = useCallback(() => setTabAsk(null), []);
  const clearToothAsk = useCallback(() => setToothAsk(null), []);

  useEffect(() => {
    const controller = new AbortController();

    setFile(null);
    setError(null);

    getPatient(patientId, controller.signal)
      .then(setFile)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not open this file.");
      });

    return () => controller.abort();
  }, [patientId]);

  const reloadVisits = useCallback(
    (signal?: AbortSignal): void => {
      listVisits(patientId, signal)
        .then((page) => {
          if (signal?.aborted !== true) setVisits(page.visits);
        })
        .catch(() => {
          // A patient with no history is the common case and is not an error; a
          // failure here must not take the chart down with it.
          if (signal?.aborted !== true) setVisits([]);
        });
    },
    [patientId],
  );

  /**
   * **The four lists the tabs count** (PF12). Each failure is quiet and reads
   * as empty: an unreadable list is not worth a red line on a file that is
   * otherwise fine, and the tab that owns it says so when opened.
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (mayPlans) {
      listPlans(patientId, signal)
        .then((rows) => {
          if (!signal.aborted) setPlans(rows);
        })
        .catch(() => {
          if (!signal.aborted) setPlans([]);
        });
    } else {
      setPlans([]);
    }
    if (mayChart) reloadVisits(signal);
    if (mayNotes) {
      listNotes(patientId, signal)
        .then((page) => {
          if (!signal.aborted) setNoteCount(page.notes.length);
        })
        .catch(() => undefined);
    }
    if (mayImaging) {
      listAttachments(patientId, signal)
        .then((page) => {
          if (!signal.aborted) setImageCount(page.attachments.length);
        })
        .catch(() => undefined);
    }

    return () => controller.abort();
  }, [patientId, reloadVisits, mayPlans, mayChart, mayNotes, mayImaging]);

  useEffect(() => {
    const controller = new AbortController();

    getToothChart(patientId, controller.signal)
      .then((chart) => {
        setPrimaryTeethShown(chart.primaryTeeth);
        setToothStates(
          Object.fromEntries(chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.state])),
        );
        setToothGlyphs(
          Object.fromEntries(chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.glyphs])),
        );
        setToothSurfaces(
          Object.fromEntries(
            chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.surfacesWorked ?? []]),
          ),
        );
        setPlannedSurfaces(
          Object.fromEntries(
            chart.teeth.map((tooth) => [tooth.fdiNumber, tooth.surfacesPlanned ?? []]),
          ),
        );
      })
      .catch(() => {
        // An unreadable chart is not a reason to hide the whole file. The chart
        // draws every tooth sound, which is what an empty chart looks like.
      });

    return () => controller.abort();
    /**
     * **Re-fetched after every write, rather than patched from the response.**
     * The editor's detail carries the *stored* state, which is not what the
     * chart draws — a booked appointment turns a tooth red without storing
     * anything (BF3), and an unstarted plan item does too (UI-TC9) — so the
     * server is the only thing that can answer. One extra request per write is
     * the cheaper mistake.
     */
  }, [patientId, chartVersion]);

  /**
   * **`?ring=` — a Patients-list row's ring, landing here** (STORY-221). The
   * list has no flags editor, details editor or Billing tab of its own, so its
   * *Add a flag*, *Edit details* and *Take payment* open the file and press the
   * file's own button. Honoured once the file has loaded, then dropped from the
   * address so a reload does not open the dialog again.
   */
  const wantedRing = searchParams.get("ring");
  useEffect(() => {
    if (file === null || wantedRing === null) return;
    if (wantedRing === "flag" && can("patients.flags.manage")) setEditingFlags(true);
    else if (wantedRing === "edit" && can("patients.edit")) setEditing(true);
    else if (wantedRing === "pay") {
      setTab("Billing");
      setTabAsk({ kind: "pay", id: nextAskId() });
    }
    setSearchParams(
      (params) => {
        params.delete("ring");
        return params;
      },
      { replace: true },
    );
  }, [file, wantedRing, can, setSearchParams]);

  /** Opens a tab and presses one of its buttons, for the quick ring. */
  const askTab = (name: TabName, ask: TabAsk): void => {
    setTab(name);
    setTabAsk(ask);
  };

  /**
   * **The file's own ring** (`page.file`, STORY-221) — anywhere in the file but
   * a tooth. Every action is a button already on the file: Book is the
   * header's link and, like it, only while nothing is booked (item 17).
   */
  useRingProvider("page", () => {
    if (file === null) return null;
    const owed = owedOf(file.outstanding);
    return {
      ring: "page.file",
      title: `${file.firstName} ${file.lastName}`.trim(),
      meta: "Patient file",
      patientId: file.id,
      subject: { type: "patient", id: file.id },
      actions: [
        file.nextAppointmentAt === null && {
          key: "book",
          label: "Book appointment",
          icon: RING_ICONS.book,
          hint: "Books their next visit",
          needs: ["appointments.book"],
          run: () => navigate(`/calendar?patient=${file.id}`),
        },
        owed.length > 0 && {
          key: "pay",
          label: "Take payment",
          icon: RING_ICONS.pay,
          hint: `They owe ${formatAmounts(owed, file.currency)}`,
          needs: ["billing.view", "billing.payment.record"],
          run: () => askTab("Billing", { kind: "pay", id: nextAskId() }),
        },
        {
          key: "note",
          label: "New note",
          icon: RING_ICONS.note,
          hint: "Writes a clinical note",
          needs: ["clinical.notes.view", "clinical.notes.write"],
          run: () => askTab("Notes", { kind: "note", id: nextAskId() }),
        },
        {
          key: "flag",
          label: "Add a flag",
          icon: RING_ICONS.flag,
          hint: "An allergy or a warning",
          needs: ["patients.flags.manage"],
          run: () => setEditingFlags(true),
        },
        {
          key: "img",
          label: "Import images",
          icon: RING_ICONS.upload,
          hint: "Adds X-rays or photos",
          needs: ["clinical.imaging.view", "clinical.imaging.upload"],
          run: () => askTab("Imaging", { kind: "images", id: nextAskId() }),
        },
        {
          key: "inv",
          label: "New invoice",
          icon: RING_ICONS.invoice,
          hint: "Starts a draft invoice",
          needs: ["billing.view", "billing.invoice.create"],
          run: () => askTab("Billing", { kind: "invoice", id: nextAskId() }),
        },
      ],
    };
  });

  /**
   * **A tooth's ring** (`file.tooth`, STORY-221). Each action opens the tooth's
   * panel and presses the panel's own button, so the panel's rules — a tooth
   * not come in yet takes no work, a baby tooth falls rather than being
   * removed — hold here too. The tooth is read first, for what is charted on
   * it: *Mark done* is offered only where exactly one planned row could be
   * meant, and a workflow starts on the newest charted row (WG-D1).
   */
  useRingProvider("file.tooth", async (element) => {
    const fdi = Number(element.dataset.tooth);
    if (file === null || !Number.isInteger(fdi)) return null;
    let detail: ToothDetail | null = null;
    try {
      detail = await getTooth(patientId, fdi);
    } catch {
      detail = null;
    }
    const state = toothStates[fdi] ?? "SOUND";
    const missing = state === "MISSING" || detail?.state === "MISSING";
    const waiting = waitingBehind(fdi, toothStates, primaryTeeth) !== null;
    const baby = isPrimaryTooth(fdi);
    const live = (detail?.procedures ?? [])
      .filter((procedure) => procedure.status !== "CANCELLED")
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    const planned = live.filter((procedure) => procedure.status === "PLANNED");
    const onlyPlanned = planned.length === 1 ? planned[0] : undefined;
    const newest = live[0];
    const charts: Permission[] = ["clinical.chart.view", "clinical.chart.edit"];
    const press = (kind: ToothAskKind, procedureId?: string) => () => {
      setTab("Chart");
      setSelectedTooth(fdi);
      setToothAsk({
        kind,
        id: nextAskId(),
        fdi,
        ...(procedureId === undefined ? {} : { procedureId }),
      });
    };
    return {
      ring: "file.tooth",
      title: `Tooth ${String(fdi)}`,
      meta: waiting
        ? "Not come in yet"
        : missing
          ? baby
            ? "Fallen / removed"
            : "Removed"
          : TOOTH_RING_META[state],
      patientId: file.id,
      ...(newest === undefined ? {} : { subject: { type: "tooth" as const, id: newest.id } }),
      actions: [
        onlyPlanned !== undefined && {
          key: "markdone",
          label: "Mark done",
          icon: RING_ICONS.check,
          hint: `Completes ${onlyPlanned.label} on ${String(fdi)} now`,
          needs: charts,
          run: press("markDone", onlyPlanned.id),
        },
        !missing &&
          !waiting && {
            key: "plan",
            label: "To be done",
            icon: RING_ICONS.tooth,
            hint: `Plans work on ${String(fdi)}`,
            needs: charts,
            run: press("planned"),
          },
        !missing &&
          !waiting && {
            key: "now",
            label: "Done now",
            icon: RING_ICONS.arrive,
            hint: `Records work done on ${String(fdi)} today`,
            needs: charts,
            run: press("done"),
          },
        missing
          ? {
              key: "back",
              label: "Return to normal",
              icon: RING_ICONS.undo,
              hint: baby ? "Undoes Mark fallen" : "Undoes Mark removed",
              needs: charts,
              run: press("normal"),
            }
          : !waiting && {
              key: "removed",
              label: baby ? "Mark fallen" : "Mark removed",
              icon: RING_ICONS.retire,
              hint: `${String(fdi)} is no longer there`,
              needs: charts,
              danger: true,
              run: press("removed"),
            },
        {
          key: "open",
          label: "Open the tooth",
          icon: RING_ICONS.target,
          hint: "Its surfaces and history",
          needs: ["clinical.chart.view"],
          run: () => {
            setTab("Chart");
            setSelectedTooth(fdi);
          },
        },
      ],
    };
  });

  /**
   * **An adult tooth has come in** (BT10): the baby tooth in front of it is
   * marked fallen, so the adult tooth takes its place on the chart and becomes
   * the tooth that is open. A double-click is all it takes, so the message
   * after it carries Undo, and Undo is *Put back* without a second Undo.
   *
   * MISSING is set on the chart at once rather than waiting for the re-read:
   * it is the one state stored exactly as asserted, so the chart cannot be
   * wrong about it, and the tooth starts moving when the click lands.
   */
  const replacePrimary = (adult: number, isUndo = false): void => {
    const baby = predecessorOf(adult);
    if (baby === null) return;
    markToothRemoved(patientId, baby)
      .then(() => {
        setToothStates((states) => ({ ...states, [baby]: "MISSING" }));
        setSelectedTooth(adult);
        setNotice({
          id: Date.now(),
          message: isUndo ? "Undone" : `Baby tooth ${String(baby)} replaced by ${String(adult)}`,
          ...(isUndo ? {} : { undo: () => putBackPrimary(adult, true) }),
        });
      })
      .catch((cause: unknown) => {
        setNotice({
          id: Date.now(),
          message:
            cause instanceof Error ? cause.message : `Baby tooth ${String(baby)} was not changed.`,
        });
      })
      .finally(() => setChartVersion((version) => version + 1));
  };

  /** Puts a replaced baby tooth back, re-derived from its own record, and opens it. */
  const putBackPrimary = (adult: number, isUndo = false): void => {
    const baby = predecessorOf(adult);
    if (baby === null) return;
    markToothNormal(patientId, baby)
      .then(() => {
        // Sound until the re-read brings what its record makes it, so the
        // teeth move back now rather than a request later.
        setToothStates((states) => ({ ...states, [baby]: "SOUND" }));
        setSelectedTooth(baby);
        setNotice({
          id: Date.now(),
          message: isUndo ? "Undone" : `Baby tooth ${String(baby)} put back`,
          ...(isUndo ? {} : { undo: () => replacePrimary(adult, true) }),
        });
      })
      .catch((cause: unknown) => {
        setNotice({
          id: Date.now(),
          message:
            cause instanceof Error ? cause.message : `Baby tooth ${String(baby)} was not changed.`,
        });
      })
      .finally(() => setChartVersion((version) => version + 1));
  };

  if (error !== null) {
    return (
      <p className={styles.empty} data-testid="patient-file-error">
        {error}
      </p>
    );
  }

  if (file === null) {
    return (
      <p className={styles.empty} data-testid="patient-file-loading">
        Opening the file…
      </p>
    );
  }

  const counts: Partial<Record<TabName, number | null>> = {
    Treatment: plans?.length ?? null,
    History: visits?.length ?? null,
    Notes: noteCount,
    Imaging: imageCount,
  };

  return (
    <div className={styles.file} data-testid="patient-file">
      {/*
        Each control only for someone who may use it (QA third run): a Dental
        nurse opened *Edit*, filled it in and had the save refused.
      */}
      <FileHeader
        file={file}
        onEdit={can("patients.edit") ? () => setEditing(true) : undefined}
        onEditFlags={can("patients.flags.manage") ? () => setEditingFlags(true) : undefined}
        canBook={can("appointments.book")}
      />

      {editing && (
        <EditPatientModal
          file={file}
          onSaved={(updated) => {
            setFile(updated);
            setEditing(false);
          }}
          onClose={() => setEditing(false)}
          // Admin only (P2 Q3) — the server enforces it and this is the
          // affordance. Passing nothing for everyone else is what hides the
          // control, rather than rendering a button that would be refused.
          onRemoved={
            can("patients.delete")
              ? () => {
                  setEditing(false);
                  navigate("/patients");
                }
              : undefined
          }
        />
      )}

      {editingFlags && (
        <FlagsModal
          patientId={patientId}
          onClose={() => setEditingFlags(false)}
          // The header's chips come from the file payload, not from the modal's
          // own list, so a change there has to re-read the file or the header
          // and the editor disagree while both are on screen.
          onChanged={() => {
            void getPatient(patientId).then(setFile);
          }}
        />
      )}

      {/*
        **Tabs say what they hold** (PF5) — a count, or on Billing the amount
        still owed, so a file is read from its tab row before a tab is opened.
      */}
      <nav className={styles.tabs} data-testid="file-tabs">
        {visibleTabs.map((name) => {
          const count = counts[name];
          return (
            <button
              key={name}
              type="button"
              className={name === shownTab ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              aria-current={name === shownTab ? "page" : undefined}
              onClick={() => setTab(name)}
              data-testid={`file-tab-${name.toLowerCase()}`}
            >
              {name}
              {name === "Billing" && owedOf(file.outstanding).length > 0 ? (
                <span className={styles.tabDue}>
                  {formatAmounts(owedOf(file.outstanding), file.currency)} due
                </span>
              ) : count !== undefined && count !== null && count > 0 ? (
                <span
                  className={styles.tabCount}
                  data-testid={`file-tab-count-${name.toLowerCase()}`}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className={styles.body} data-testid="tab-panel">
        {visibleTabs.length === 0 ? (
          <p className={styles.empty} data-testid="file-no-tabs">
            Nothing in this file is open to you beyond the details above and the flags.
          </p>
        ) : shownTab === "Chart" ? (
          <ChartTab
            toothStates={toothStates}
            toothGlyphs={toothGlyphs}
            toothSurfaces={toothSurfaces}
            plannedSurfaces={plannedSurfaces}
            selectedTooth={selectedTooth}
            onSelectTooth={(fdi) => setSelectedTooth((current) => (current === fdi ? null : fdi))}
            primaryTeeth={primaryTeeth}
            onPrimaryTeethChange={
              can("clinical.chart.edit")
                ? (shown) => {
                    // Shown at once, then re-read: the server is what decides, and
                    // the editor closes because the tooth it showed may now be
                    // hidden or held back.
                    setPrimaryTeethShown(shown);
                    setSelectedTooth(null);
                    setPrimaryTeeth(patientId, shown)
                      .catch(() => undefined)
                      .finally(() => setChartVersion((version) => version + 1));
                  }
                : undefined
            }
            ringKind="file.tooth"
            onReplacePrimary={(adult) => {
              // Double-clicking a tooth in is charting too (QA third run).
              if (can("clinical.chart.edit")) replacePrimary(adult);
            }}
            editor={
              selectedTooth === null
                ? null
                : (beside) => (
                    <ToothEditor
                      patientId={patientId}
                      fdiNumber={selectedTooth}
                      onChanged={() => setChartVersion((version) => version + 1)}
                      onClose={() => setSelectedTooth(null)}
                      wide
                      beside={beside}
                      ask={toothAsk !== null && toothAsk.fdi === selectedTooth ? toothAsk : null}
                      onAsked={clearToothAsk}
                      {...(primaryTeeth
                        ? {
                            primary: {
                              waitingBehind: waitingBehind(
                                selectedTooth,
                                toothStates,
                                primaryTeeth,
                              ),
                              replaced: replacedBaby(selectedTooth, toothStates, primaryTeeth),
                              onReplace: (adult: number) => replacePrimary(adult),
                              onPutBack: (adult: number) => putBackPrimary(adult),
                            },
                          }
                        : {})}
                    />
                  )
            }
            visits={visits ?? []}
            plans={plans ?? []}
            onOpenVisit={setOpenVisit}
            onTab={setTab}
          />
        ) : shownTab === "Treatment" ? (
          plans === null ? (
            <p className={styles.empty}>Reading&hellip;</p>
          ) : (
            <TreatmentTab
              plans={plans}
              open={treatmentOpen}
              onToggle={(planId, next) =>
                setTreatmentOpen((current) => new Map(current).set(planId, next))
              }
              onChanged={(updated) => {
                setPlans((current) =>
                  (current ?? []).map((plan) => (plan.id === updated.id ? updated : plan)),
                );
                // Plan work may have billed (PP-D8): the header's due and
                // drafted figures are re-read, not left as they opened.
                getPatient(patientId)
                  .then(setFile)
                  .catch(() => {
                    /* The header keeps its last figures; the plan itself is right. */
                  });
              }}
            />
          )
        ) : shownTab === "History" ? (
          <HistoryTab
            patientId={patientId}
            visits={visits}
            version={chartVersion}
            onOpenVisit={setOpenVisit}
          />
        ) : shownTab === "Notes" ? (
          <NotesTab
            patientId={patientId}
            onCount={setNoteCount}
            ask={tabAsk?.kind === "note" ? tabAsk : null}
            onAsked={clearTabAsk}
          />
        ) : shownTab === "Imaging" ? (
          <ImagingTab
            patientId={patientId}
            openImageId={openImageId}
            onOpened={() => {
              setOpenImageId(null);
            }}
            onCount={setImageCount}
            ask={tabAsk?.kind === "images" ? tabAsk : null}
            onAsked={clearTabAsk}
          />
        ) : (
          <BillingTab
            patientId={patientId}
            ask={tabAsk?.kind === "pay" || tabAsk?.kind === "invoice" ? tabAsk : null}
            onAsked={clearTabAsk}
          />
        )}
      </div>

      {openVisit !== null && (
        <VisitDialog
          patientId={patientId}
          date={openVisit}
          onClose={() => setOpenVisit(null)}
          onOpenImage={(attachmentId) => {
            setOpenVisit(null);
            setOpenImageId(attachmentId);
            setTab("Imaging");
          }}
          onChanged={() => {
            // Completing a visit bills it and moves its appointment on, so the
            // header's balance, the visit list and the chart all change.
            reloadVisits();
            setChartVersion((version) => version + 1);
            void getPatient(patientId).then(setFile);
          }}
        />
      )}

      {notice !== null && (
        <UndoToast
          key={notice.id}
          message={notice.message}
          {...(notice.undo === undefined ? {} : { onUndo: notice.undo })}
          onDismiss={() => setNotice(null)}
          testId="tooth-notice"
        />
      )}
    </div>
  );
}

/**
 * **The header is two lines** (PF6).
 *
 * The name with its **critical** flags beside it; informational flags fold
 * behind *+N more*, which lists every flag with its detail and offers *Edit
 * flags*. Under it one quiet line of facts — file number, the Chatbot mark, sex
 * and phone, insurer — and, at its right, where the patient stands: balance
 * (warm only when owed), next appointment, recall.
 */
function FileHeader({
  file,
  onEdit,
  onEditFlags,
  canBook,
}: {
  file: PatientFileWithGlance;
  /** Absent without `patients.edit`, and so is the button. */
  onEdit?: () => void;
  /** Absent without `patients.flags.manage`. */
  onEditFlags?: () => void;
  canBook: boolean;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  const critical = file.flags.filter((flag) => flag.severity === "CRITICAL");
  const other = file.flags.filter((flag) => flag.severity !== "CRITICAL");

  return (
    <header className={styles.header}>
      <div className={styles.line1}>
        <h1 className={styles.name} data-testid="patient-name">
          {file.firstName} {file.lastName}
        </h1>

        <div className={styles.flags} data-testid="patient-flags">
          {critical.map((flag) => (
            <FlagChip key={flag.id} flag={flag} />
          ))}
          {other.length > 0 ? (
            <MoreFlags flags={file.flags} hidden={other.length} onEditFlags={onEditFlags} />
          ) : (
            /*
              QA #37 — the flags API always worked and nothing called it, so a
              clinic with no seed data could never record an allergy. The
              control sits with the chips because a flag is not a demographic:
              it is the thing the rest of this file is read against.
            */
            onEditFlags !== undefined && (
              <button
                type="button"
                className={styles.flagEdit}
                onClick={onEditFlags}
                data-testid="edit-flags"
              >
                {file.flags.length === 0 ? "Add a flag" : "Edit flags"}
              </button>
            )
          )}
        </div>

        <div className={styles.actions}>
          {/*
            **Book, only when nothing is booked** (item 17): a patient who
            already has a date usually needs it moved rather than doubled, and
            that is the calendar's job. A link rather than a form — one place
            decides what booking means.
          */}
          {canBook && file.nextAppointmentAt === null && (
            <Link
              to={`/calendar?patient=${file.id}`}
              className={styles.book}
              data-testid="glance-book"
            >
              Book an appointment
            </Link>
          )}
          {onEdit !== undefined && (
            <button
              type="button"
              className={styles.edit}
              onClick={onEdit}
              data-testid="edit-patient"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className={styles.line2} data-testid="at-a-glance">
        <div className={styles.facts}>
          <span className={styles.fileNumber} data-testid="file-number" aria-label="File number">
            {file.fileNumberDisplay}
          </span>
          {/*
            Only ever drawn for a file the assistant opened. A staff-typed file
            gets no chip at all — the thing worth saying is the exception.
          */}
          {file.source === "WHATSAPP" && (
            <span
              className={styles.sourceChip}
              data-testid="patient-source"
              title="Opened by the WhatsApp assistant from what the caller said. The name and number on it have not been checked by anyone here."
            >
              Chatbot
            </span>
          )}
          <span data-testid="patient-contact">{contactLine(file)}</span>
          <span data-testid="patient-insurer">{file.insurerName ?? "No insurer recorded"}</span>
        </div>
        <div className={styles.status}>
          {/* Null for anyone without billing.view (STORY-170), so not drawn. */}
          {file.outstanding !== null && (
            <span
              className={owedOf(file.outstanding).length > 0 ? styles.due : undefined}
              data-testid="patient-balance"
            >
              {formatBalance(file.outstanding)}
            </span>
          )}
          {/*
            **Drafted beside due** (PP-D8): plan work billed onto a draft is owed
            by nobody until it is issued, and would otherwise be seen nowhere.
          */}
          {file.drafted !== null && owedOf(file.drafted).length > 0 && (
            <span data-testid="patient-drafted">
              {formatAmounts(owedOf(file.drafted), file.currency)} drafted
            </span>
          )}
          <span data-testid="patient-next">
            {file.nextAppointmentAt === null ? (
              "No appointment booked"
            ) : (
              <>
                Next:{" "}
                <b>
                  {new Date(file.nextAppointmentAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                    // The location's clock, as the calendar draws it (P2 Q8).
                    timeZone: timezone,
                  })}
                </b>
              </>
            )}
          </span>
          <span data-testid="patient-recall">
            {file.recallDueOn === null
              ? "Recall: set at first exam"
              : `Recall due ${new Date(file.recallDueOn).toLocaleDateString(undefined, {
                  dateStyle: "medium",
                  // A calendar date, parsed as UTC midnight: read it in UTC or
                  // a browser west of Greenwich shows the day before.
                  timeZone: "UTC",
                })}`}
          </span>
        </div>
      </div>
    </header>
  );
}

function FlagChip({ flag }: { flag: PatientFlagSummary }): React.JSX.Element {
  return (
    <span
      className={
        flag.severity === "CRITICAL" ? `${styles.flag} ${styles.flagCritical}` : styles.flag
      }
      // The detail carries the consequence — "clindamycin is this file's
      // alternative" — so it is the chip's title rather than being dropped.
      title={flag.detail ?? undefined}
      data-severity={flag.severity}
      data-testid="patient-flag"
    >
      {flag.label}
    </span>
  );
}

/**
 * *+N more*: every flag, critical first, with its detail — and *Edit flags*.
 * Closes on a click outside it or on Escape.
 */
function MoreFlags({
  flags,
  hidden,
  onEditFlags,
}: {
  flags: readonly PatientFlagSummary[];
  hidden: number;
  onEditFlags?: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent): void => {
      if (wrap.current !== null && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className={styles.flagWrap} ref={wrap}>
      <button
        type="button"
        className={styles.flagEdit}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
        data-testid="flags-more"
      >
        +{hidden} more
      </button>
      {open && (
        <div
          className={styles.flagPop}
          role="dialog"
          aria-label="Flags"
          data-testid="flags-popover"
        >
          <ul className={styles.flagList}>
            {flags.map((flag) => (
              <li key={flag.id}>
                <b
                  className={flag.severity === "CRITICAL" ? styles.flagListCritical : undefined}
                  data-testid="patient-flag-detail"
                  data-severity={flag.severity}
                >
                  {flag.label}
                </b>
                <span>
                  {flag.detail ?? (flag.severity === "CRITICAL" ? "Critical" : "For information")}
                </span>
              </li>
            ))}
          </ul>
          {onEditFlags !== undefined && (
            <button
              type="button"
              className={styles.edit}
              onClick={() => {
                setOpen(false);
                onEditFlags();
              }}
              data-testid="edit-flags"
            >
              Edit flags
            </button>
          )}
        </div>
      )}
    </span>
  );
}

/**
 * **The chart fills the tab** (WC1–WC4, 2026-10-28). Anthony: the chart was too
 * small. Nothing sits under it any more: *Last visits* and *In progress* are
 * buttons in the chart's footer that open a popup each (WC3), and a picked
 * tooth's editor opens as a panel over one side of the chart — the side away
 * from the tooth, so the tooth stays in view (WC2). The chart never moves or
 * resizes when a tooth is opened (PF7's rule, kept).
 */
function ChartTab({
  toothStates,
  toothGlyphs,
  toothSurfaces,
  plannedSurfaces,
  selectedTooth,
  onSelectTooth,
  primaryTeeth,
  onPrimaryTeethChange,
  onReplacePrimary,
  ringKind,
  editor,
  visits,
  plans,
  onOpenVisit,
  onTab,
}: {
  toothStates: Partial<Record<number, ChartToothStateName>>;
  toothGlyphs: Partial<Record<number, ToothGlyphName[]>>;
  toothSurfaces: Partial<Record<number, string[]>>;
  plannedSurfaces: Partial<Record<number, string[]>>;
  selectedTooth: number | null;
  onSelectTooth: (fdi: number) => void;
  primaryTeeth: boolean;
  /** Absent without `clinical.chart.edit`, and so is the switch. */
  onPrimaryTeethChange?: (shown: boolean) => void;
  /** A faded adult tooth double-clicked: it has come in (BT10). */
  onReplacePrimary: (adultFdi: number) => void;
  /** What the quick ring calls a tooth here (STORY-221). */
  ringKind?: string;
  /** The tooth editor, told whether it sits beside the chart. Null while no tooth is open. */
  editor: ((beside: boolean) => React.ReactNode) | null;
  visits: readonly VisitSummary[];
  plans: readonly TreatmentPlan[];
  onOpenVisit: (date: string) => void;
  onTab: (tab: TabName) => void;
}): React.JSX.Element {
  const [ref, size] = useSize<HTMLDivElement>();

  return (
    <div
      className={styles.chartTab}
      ref={ref}
      data-testid="chart-tab"
      style={{ "--side": `${String(panelWidth(size.width))}px` } as React.CSSProperties}
    >
      <ToothChart
        states={toothStates}
        glyphs={toothGlyphs}
        surfaces={toothSurfaces}
        plannedSurfaces={plannedSurfaces}
        selectedTooth={selectedTooth}
        onSelectTooth={onSelectTooth}
        primaryTeeth={primaryTeeth}
        onPrimaryTeethChange={onPrimaryTeethChange}
        onReplacePrimary={onReplacePrimary}
        ringKind={ringKind}
        fit
        actions={
          <ChartPopups visits={visits} plans={plans} onOpenVisit={onOpenVisit} onTab={onTab} />
        }
      />
      {editor !== null && selectedTooth !== null && (
        <div
          className={styles.toothPanel}
          data-dock={panelDock(selectedTooth)}
          data-testid="tooth-panel"
        >
          {editor(true)}
        </div>
      )}
    </div>
  );
}

/**
 * *Last visits* and *In progress* as two buttons in the chart's footer, each
 * opening its list in a popup (WC3). A button is left out when it would open on
 * nothing. The popup holds the five latest visits and every treatment in
 * progress, with *Full history* and *All treatment* to the tabs that hold the rest.
 */
function ChartPopups({
  visits,
  plans,
  onOpenVisit,
  onTab,
}: {
  visits: readonly VisitSummary[];
  plans: readonly TreatmentPlan[];
  onOpenVisit: (date: string) => void;
  onTab: (tab: TabName) => void;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  const going = plans.filter(inProgress);
  const lastVisits = visits.slice(0, 5);
  const last = visits[0];

  return (
    <>
      {last !== undefined && (
        <ChartPopup
          label={
            <>
              Last visit <span className={styles.popupWhen}>{formatDate(last.date)}</span>
            </>
          }
          name="Last visits"
          testId="last-visits"
        >
          <div className={styles.cardHead}>
            <h3>{lastVisits.length === 1 ? "Last visit" : "Last visits"}</h3>
            <button
              type="button"
              className={styles.cardLink}
              onClick={() => onTab("History")}
              data-testid="last-visits-all"
            >
              Full history
            </button>
          </div>
          {lastVisits.map((visit) => (
            <button
              key={visit.date}
              type="button"
              className={styles.lastVisit}
              onClick={() => onOpenVisit(visit.date)}
              data-card=""
              data-testid="last-visit"
              {...visitRowData(visit)}
            >
              <span className={styles.lvDate}>{formatDate(visit.date)}</span>
              <span className={styles.lvTitle}>{visit.title ?? "Visit"}</span>
              <span className={styles.lvBy}>{visit.providerName ?? visit.branchName ?? ""}</span>
              <span className={styles.lvCounts}>{countsOf(visit)}</span>
            </button>
          ))}
        </ChartPopup>
      )}

      {going.length > 0 && (
        <ChartPopup
          label={
            <>
              In progress <span className={styles.popupCount}>{going.length}</span>
            </>
          }
          name="In progress"
          testId="in-progress"
        >
          <div className={styles.cardHead}>
            <h3>In progress</h3>
            <span className={styles.cardSub}>{going.length}</span>
            <button
              type="button"
              className={styles.cardLink}
              onClick={() => onTab("Treatment")}
              data-testid="in-progress-all"
            >
              All treatment
            </button>
          </div>
          {going.map((plan) => {
            const standing = standingOf(plan, timezone);
            const bookable = plan.parts.find(
              (part) => part.items.length > 0 && part.completedAt === null && part.booking === null,
            );
            return (
              <div key={plan.id} className={styles.inProgress} data-testid="in-progress-row">
                <span className={styles.ipTitle}>{plan.title ?? "Treatment"}</span>
                {bookable !== undefined && (
                  <Link
                    to={`/calendar?patient=${plan.patientId}&visit=${plan.id}:${bookable.id}`}
                    className={styles.ipBook}
                    data-testid="in-progress-book"
                  >
                    Book visit {plan.parts.indexOf(bookable) + 1}
                  </Link>
                )}
                <span className={styles.ipStanding}>
                  <Segments parts={plan.parts} />
                  {standing.summary}
                </span>
              </div>
            );
          })}
        </ChartPopup>
      )}
    </>
  );
}

/** One footer button and its popup, drawn over the page so no card clips it. */
function ChartPopup({
  label,
  name,
  testId,
  children,
}: {
  label: React.ReactNode;
  name: string;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const close = useCallback((how: "outside" | "escape") => {
    setOpen(false);
    if (how === "escape") button.current?.focus();
  }, []);
  useDismiss(open, close, button, panel);
  const place = useAnchor(open, button, "end");

  return (
    <>
      <button
        ref={button}
        type="button"
        className={styles.popupButton}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
        data-testid={`${testId}-button`}
      >
        {label}
        <span aria-hidden className={styles.popupCaret}>
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            className={styles.popup}
            role="dialog"
            aria-label={name}
            style={place}
            data-testid={testId}
            // Anything chosen in it — a visit, a tab, *Book visit* — closes it.
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button, a") !== null) setOpen(false);
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/** `F · +447700900742` — parts it does not have are dropped. */
function contactLine(file: PatientFileWithGlance): string {
  return [file.sex === null ? null : file.sex.charAt(0), formatPhone(file.phoneE164)]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/**
 * Money, from integer minor units, through the shared formatter — so the header
 * and the Billing tab cannot disagree about the same figure. The exponent comes
 * from the currency, never from an assumption of two decimal places.
 */
function formatBalance(amounts: readonly MoneyInCurrency[]): string {
  // **Per currency, never one total across them** (QA4-D6): a patient seen at
  // locations in two currencies can owe in one and be in credit in the other.
  const due = owedOf(amounts);
  const credit = amounts.filter((a) => a.cents < 0).map((a) => ({ ...a, cents: -a.cents }));
  if (due.length === 0 && credit.length === 0) return "No balance";
  return [
    due.length > 0 ? `Balance due ${formatAmounts(due, "")}` : null,
    credit.length > 0 ? `In credit ${formatAmounts(credit, "")}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/** The currencies in which something is still owed. Null (no billing.view) is none. */
function owedOf(amounts: readonly MoneyInCurrency[] | null): MoneyInCurrency[] {
  return (amounts ?? []).filter((a) => a.cents > 0);
}
